/**
 * Matching a user's connected accounts against a requested selector
 * (connectionId / provider / fromEmail). PURE and dependency-free.
 *
 * These helpers only FILTER and DESCRIBE. Deciding what zero / one / many
 * matches MEAN is `selectOneAccount` in ./selection.ts — keeping the two apart
 * is what stops every caller re-inventing the policy (and disagreeing about it).
 */

import { normalizeProviderAlias } from "./provider-aliases.js";

/**
 * One connected account, reduced to the fields any selector can see.
 *
 * Deliberately structural: hub rows, the Meridian send context, the
 * workspace-addon account list and the desktop account list all satisfy it
 * without conversion, so none of them needs a private copy of this logic.
 */
export interface ConnectedAccount {
  connectionId: string;
  provider: string;
  providerConfigKey?: string | null;
  email?: string | null;
  /**
   * True when this mailbox belongs to a TEAMMATE and was shared with the
   * caller, not authorized by them. Used for DESCRIPTION and for
   * {@link partitionAccounts}; it is never a matching criterion, because a
   * shared mailbox is a legitimate sender.
   */
  isShared?: boolean;
}

/** What the caller asked for. Every field optional; blank fields are ignored. */
export interface AccountSelector {
  connectionId?: string | undefined;
  provider?: string | undefined;
  fromEmail?: string | undefined;
}

/** Does an account satisfy a requested provider, tolerating aliases? */
export function providerMatchesAccount(
  provider: string,
  account: Pick<ConnectedAccount, "provider" | "providerConfigKey">
): boolean {
  const wanted = normalizeProviderAlias(provider);
  return (
    normalizeProviderAlias(account.provider) === wanted ||
    (typeof account.providerConfigKey === "string" &&
      normalizeProviderAlias(account.providerConfigKey) === wanted)
  );
}

/** True when the selector names any account constraint at all. */
export function hasAccountSelector(selector: AccountSelector): boolean {
  return Boolean(
    selector.connectionId?.trim() || selector.provider?.trim() || selector.fromEmail?.trim()
  );
}

/**
 * Filter accounts to those satisfying EVERY provided selector field
 * (connectionId exact, provider alias-tolerant, fromEmail case-insensitive).
 * Blank/whitespace fields are ignored. With no fields, returns all accounts.
 */
export function filterAccountsBySelector<T extends ConnectedAccount>(
  accounts: readonly T[],
  selector: AccountSelector
): T[] {
  const wantConnectionId = selector.connectionId?.trim() || undefined;
  const wantProvider = selector.provider?.trim() || undefined;
  const wantFromEmail = selector.fromEmail?.trim().toLowerCase() || undefined;

  return accounts.filter((account) => {
    if (wantConnectionId && account.connectionId !== wantConnectionId) return false;
    if (wantProvider && !providerMatchesAccount(wantProvider, account)) return false;
    if (wantFromEmail && (account.email ?? "").trim().toLowerCase() !== wantFromEmail) return false;
    return true;
  });
}

/**
 * Normalized mailbox identity — the address + canonical provider an account
 * actually points at. Null when the address is unknown, since two accounts with
 * no address on file cannot be PROVEN to be the same inbox.
 */
function mailboxIdentity(account: ConnectedAccount): string | null {
  const email = account.email?.trim().toLowerCase();
  if (!email) return null;
  return `${normalizeProviderAlias(account.provider)}::${email}`;
}

/**
 * The stable member of an interchangeable set: lowest connectionId.
 *
 * NOT `accounts[0]`. Array order here comes from whichever query ran first
 * (owned-by-email before owned-by-auth-user, say), so `[0]` silently means
 * "whichever mailbox happens to sort first in an assembly order nobody chose",
 * and it changes under the caller when an unrelated query is reordered.
 */
export function pickInterchangeableAccount<T extends ConnectedAccount>(
  accounts: readonly T[]
): T | undefined {
  if (accounts.length === 0) return undefined;
  return [...accounts].sort((a, b) => a.connectionId.localeCompare(b.connectionId))[0];
}

/**
 * Collapse accounts that point at the SAME mailbox down to one.
 *
 * A provider mints a brand-new connection every time a mailbox is
 * re-authorized, so a single physical inbox is routinely represented by several
 * connections (one account here was connected 3x). Those siblings are
 * indistinguishable to the user — same address, same provider, same mail — but
 * they made every selector ambiguous, so `fromEmail`/`provider` errored with
 * "more than one account matches" and the mailbox became unreachable: the
 * connectionId the error demanded was not obtainable from any tool.
 *
 * For READ-ONLY operations the siblings are interchangeable, so picking one is
 * correct rather than merely convenient. The pick is deterministic (lowest
 * connectionId) so repeated calls hit the same connection and stay cache-warm.
 *
 * Send paths may use it too, but ONLY on an already-filtered match set:
 * collapsing siblings of the one mailbox the caller named cannot pick a
 * different account. What must never be collapsed on a send path is ambiguity
 * across DIFFERENT mailboxes; that still fails loud, because listing the wrong
 * inbox is recoverable and sending from the wrong account is not.
 *
 * Accounts whose address is unknown are never collapsed, and a genuinely
 * ambiguous set (two DIFFERENT mailboxes) is returned untouched so the caller
 * still errors.
 */
export function collapseSameMailboxAccounts<T extends ConnectedAccount>(
  accounts: readonly T[]
): T[] {
  if (accounts.length <= 1) return [...accounts];

  const identities = accounts.map(mailboxIdentity);
  if (identities.some((id) => id === null)) return [...accounts];
  if (new Set(identities).size > 1) return [...accounts];

  const picked = pickInterchangeableAccount(accounts);
  return picked ? [picked] : [...accounts];
}

/**
 * Split a list into the caller's OWN accounts and those shared with them.
 *
 * Both halves are ARRAYS. That is the whole point: the CRM Integrations card
 * rendered `accounts.find((a) => !a.isShared)` and so displayed exactly one
 * owned mailbox per provider forever — a personal Gmail connected on top of a
 * work Gmail simply never appeared, under a button reading "Add Another
 * Connection". A function that cannot return a second account cannot hide one.
 */
export function partitionAccounts<T extends ConnectedAccount>(
  accounts: readonly T[]
): { owned: T[]; shared: T[] } {
  const owned: T[] = [];
  const shared: T[] = [];
  for (const account of accounts) {
    if (account.isShared) shared.push(account);
    else owned.push(account);
  }
  return { owned, shared };
}

/** Human-readable list of accounts, for disambiguation/error copy. */
export function describeAccounts(accounts: readonly ConnectedAccount[]): string {
  if (accounts.length === 0) return "(no email accounts connected)";
  return accounts
    .map((account) => {
      // Naming a shared mailbox as shared is what makes an ambiguous list
      // actionable. A user with one Gmail account of their own plus two shared
      // with them by teammates reads "three google-mail accounts" as a bug;
      // "…, shared" says which two are not theirs, so both the model and the
      // person can pick the right one on the retry.
      const shared = account.isShared ? ", shared" : "";
      return `${account.email ?? "unknown address"} (${account.provider}${shared})`;
    })
    .join(", ");
}

/** A concise render of a requested selector, for error messages. */
export function describeAccountSelector(selector: AccountSelector): string {
  return (
    [
      selector.fromEmail ? `address "${selector.fromEmail}"` : null,
      selector.provider ? `provider "${selector.provider}"` : null,
      selector.connectionId ? `connection "${selector.connectionId}"` : null,
    ]
      .filter(Boolean)
      .join(", ") || "(no selector)"
  );
}

/**
 * A sender the caller is allowed to send from — the shape returned to the model
 * on a failed resolve so it can immediately retry with a valid `fromEmail`.
 */
export interface PermittedSender {
  /** Canonical provider, e.g. "google-mail", "outlook-oauth". */
  provider: string;
  /** The connected address to pass as `fromEmail` (null when none is on file). */
  fromEmail: string | null;
}

/** Structured list of the accounts the user may send from, for error feedback. */
export function toPermittedSenders(accounts: readonly ConnectedAccount[]): PermittedSender[] {
  return accounts.map((account) => ({
    provider: account.provider,
    fromEmail: account.email ?? null,
  }));
}
